import { ApiError, ISTEK_ZAHTJEVA, MREZNA_GRESKA, problemUGresku, type ProblemDocument } from './problem'

/**
 * Najniži sloj: jedan `fetch`, jedno čitanje odgovora, problem+json → `ApiError`.
 * Ovdje NEMA tokena ni osvježavanja sesije — to radi `client.ts`, jer i sam
 * `/auth/refresh` mora moći proći ovuda a da ne pokrene sam sebe.
 */

/** Relativna baza: statiku i API u produkciji poslužuje isti host (Caddy). */
export const API_BASE = '/api/v1'

/**
 * Rok za odgovor. `fetch` sam po sebi NEMA istek: veza koja se otvori i tiho
 * stoji visi dovijeka, a s njom i ekran koji je čeka — na devu se tako objesio
 * ekran za upis SMS koda (zadatak 10 §3), jer se `/auth/verify` nikad nije
 * vratio ni s odgovorom ni s greškom. Petnaest sekundi je iznad svakog normalnog
 * poziva ovog API-ja (najsporiji na devu je ispod 100 ms) i ispod granice na
 * kojoj gost zaključi da je aplikacija mrtva.
 */
export const ISTEK_MS = 15_000

/**
 * Rok za SLANJE SLIKE (dotjerivanje-12 §1). Petnaest sekundi je granica za JSON
 * od par stotina bajtova; fotografija od 2 MB preko hotelskog wifija normalno
 * putuje dulje, pa bi ista granica prijavljivala kvar nad uploadom koji radi.
 * Šezdeset sekundi je iznad toga i ispod granice na kojoj gost odustane.
 */
export const ISTEK_SLIKE_MS = 60_000

export interface RawZahtjev {
  metoda?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  tijelo?: unknown
  /**
   * `multipart/form-data` tijelo (dotjerivanje-12 §1) — slika.
   *
   * Vlastito polje, a ne `tijelo`, zbog JEDNE stvari: `Content-Type` se NAMJERNO
   * ne postavlja. Granicu multiparta upisuje preglednik sam, a ručno postavljeno
   * zaglavlje bi ju izostavilo i poslužitelj bi tijelo pročitao kao prazno.
   * Ovako se razlika vidi u tipu, a ne u komentaru.
   */
  obrazac?: FormData
  token?: string | null
  jezik?: string
  signal?: AbortSignal
  /** Zaglavlja specifična za jedan poziv — zasad samo `Idempotency-Key` (CLAUDE.md §8). */
  dodatnaZaglavlja?: Record<string, string>
}

export async function raw<T>(putanja: string, zahtjev: RawZahtjev = {}): Promise<T> {
  const { metoda = 'GET', tijelo, obrazac, token, jezik, signal, dodatnaZaglavlja } = zahtjev

  const zaglavlja: Record<string, string> = { Accept: 'application/json' }
  if (tijelo !== undefined) zaglavlja['Content-Type'] = 'application/json'
  if (token) zaglavlja['Authorization'] = `Bearer ${token}`
  // API vraća lokalizirane detalje po ovom zaglavlju (API §0); `title` ostaje ključ.
  if (jezik) zaglavlja['Accept-Language'] = jezik
  Object.assign(zaglavlja, dodatnaZaglavlja)

  const rok = new AbortController()
  const mjerac = setTimeout(() => rok.abort(), obrazac ? ISTEK_SLIKE_MS : ISTEK_MS)

  let odgovor: Response
  try {
    odgovor = await fetch(`${API_BASE}${putanja}`, {
      method: metoda,
      headers: zaglavlja,
      body: obrazac ?? (tijelo === undefined ? undefined : JSON.stringify(tijelo)),
      signal: spoji(signal, rok.signal),
    })
  } catch (greska) {
    // Prekid nije kvar mreže nego odluka pozivatelja — ne pretvara se u poruku.
    if (signal?.aborted) throw greska
    // Istek roka jest kvar, i to onaj koji korisnik mora vidjeti: bez njega
    // ekran ostaje na vrtuljku dok ga netko ne osvježi rukom.
    if (rok.signal.aborted) throw new ApiError(0, ISTEK_ZAHTJEVA)
    throw new ApiError(0, MREZNA_GRESKA)
  } finally {
    clearTimeout(mjerac)
  }

  if (odgovor.status === 204) return undefined as T

  const tekst = await odgovor.text()
  const podaci = tekst ? bezopasanJson(tekst) : null

  if (!odgovor.ok) {
    throw problemUGresku(odgovor.status, podaci as ProblemDocument | null)
  }

  return podaci as T
}

/**
 * Prekid pozivatelja (otkazana ponuda) i rok odgovora su dva razloga za isti
 * `AbortSignal`. `AbortSignal.any` postoji, ali ne u svakom pregledniku koji
 * gost donese na odmor, pa se spajaju ručno — a kad pozivatelj signal nije ni
 * poslao, ne gradi se ništa.
 */
function spoji(pozivatelj: AbortSignal | undefined, rok: AbortSignal): AbortSignal {
  if (!pozivatelj) return rok
  if (pozivatelj.aborted) return pozivatelj

  const oba = new AbortController()
  const prekini = () => oba.abort()

  pozivatelj.addEventListener('abort', prekini, { once: true })
  rok.addEventListener('abort', prekini, { once: true })

  return oba.signal
}

/** Odgovor koji nije JSON (npr. HTML s posrednika) ne smije srušiti klijenta. */
function bezopasanJson(tekst: string): unknown {
  try {
    return JSON.parse(tekst)
  } catch {
    return null
  }
}
